import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from '@/common/ui/card'
import { Alert, AlertDescription } from '@/common/ui/alert'
import { Switch } from '@/common/ui/switch'
import { Label } from '@/common/ui/label'
import { Badge } from '@/common/ui/badge'
import { Skeleton } from '@/common/ui/skeleton'
import useQueryGetGeneralSetting from '@/modules/client-setting/api/hooks/useQueryGetGeneralSetting'
import useMutationUpdateGeneralSetting from '@/modules/client-setting/api/hooks/useMutationUpdateGeneralSetting'
import { GeneralSettingQueryKeys } from '@/modules/client-setting/keys'
import showErrorToast from '@/common/utils/showErrorToast'
import { Wallet } from 'lucide-react'

type ProviderType = 'pg' | 'joker' | 'jili' | 'pp' | 'nlc'

export default function MainWalletProviderVersionSettings() {
	const queryClient = useQueryClient()
	const { data: generalSetting, isLoading } = useQueryGetGeneralSetting()
	const { mutateAsync: updateGeneralSetting } =
		useMutationUpdateGeneralSetting()
	const [loadingProvider, setLoadingProvider] = useState<ProviderType | null>(
		null,
	)

	const handleProviderToggle = async (
		field: string,
		value: boolean,
		type: ProviderType,
	) => {
		setLoadingProvider(type)
		try {
			const payload: Record<string, boolean> = {
				[field]: value,
			}

			await updateGeneralSetting({ payload })
			queryClient.invalidateQueries({
				queryKey: [GeneralSettingQueryKeys.GetGeneralSetting],
			})
			toast.success('อัพเดทการตั้งค่าค่ายเกมสำเร็จ')
		} catch (error) {
			showErrorToast(error as Error)
		} finally {
			setLoadingProvider(null)
		}
	}

	if (isLoading || !generalSetting) {
		return <Skeleton className="h-64 w-full" />
	}

	const mainWalletProviders = [
		{
			type: 'pg' as ProviderType,
			name: 'PG',
			checked: generalSetting.mainWalletPgHard,
			field: 'mainWalletPgHard' as const,
		},
		{
			type: 'joker' as ProviderType,
			name: 'Joker',
			checked: generalSetting.mainWalletJokerHard,
			field: 'mainWalletJokerHard' as const,
		},
		{
			type: 'jili' as ProviderType,
			name: 'JILI',
			checked: generalSetting.mainWalletJiliHard,
			field: 'mainWalletJiliHard' as const,
		},
		{
			type: 'pp' as ProviderType,
			name: 'PP',
			checked: (generalSetting as any).mainWalletPpHard ?? false,
			field: 'mainWalletPpHard' as const,
		},
		{
			type: 'nlc' as ProviderType,
			name: 'NLC',
			checked: (generalSetting as any).mainWalletNlcHard ?? false,
			field: 'mainWalletNlcHard' as const,
		},
	]

	return (
		<Card className="border-blue-200 dark:border-blue-800">
			<CardHeader>
				<div className="flex items-center gap-2">
					<Wallet className="h-5 w-5 text-blue-600 dark:text-blue-400" />
					<CardTitle>เวอร์ชันค่ายเกมตอนฝากไม่รับโปร</CardTitle>
				</div>
				<CardDescription>
					กำหนดว่าผู้เล่นที่เล่นจากการฝากไม่รับโปร
					จะเล่นเวอร์ชั่นใหม่หรือเวอร์ชั่นเริ่มต้น
				</CardDescription>
			</CardHeader>
			<CardContent className="space-y-4">
				<Alert className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950">
					<AlertDescription className="text-xs text-blue-800 dark:text-blue-200">
						<p>
							<strong>เวอร์ชั่นใหม่</strong> =
							ระบบค่ายเกมของทางเราที่สามารถตั้งค่าการปรับแตกได้ •{' '}
							<strong>เวอร์ชั่นเริ่มต้น</strong> = ค่ายเกมจากผู้ให้บริการ
							(ไม่สามารถปรับแตกได้)
						</p>
					</AlertDescription>
				</Alert>

				<Alert className="border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-950">
					<AlertDescription className="text-xs text-orange-800 dark:text-orange-200">
						<strong>⚠️ สำคัญ:</strong>{' '}
						การตั้งค่านี้จะมีผลกับผู้เล่นที่เล่นจากกระเป๋าไม่โปรเท่านั้น
						(ไม่ใช้โปรโมชั่น) • การตั้งค่าสำหรับโปรโมชั่นและอีเว้นท์ต่างๆ
						จะต้องตั้งแยกต่างหาก
					</AlertDescription>
				</Alert>

				<div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
					{mainWalletProviders.map((provider) => (
						<div
							key={provider.type}
							className="flex items-center justify-between rounded-lg border p-4"
						>
							<div className="flex items-center gap-3">
								<Label
									htmlFor={`main-${provider.type}-toggle`}
									className="cursor-pointer text-base font-semibold"
								>
									{provider.name}
								</Label>
								<Badge
									variant="outline"
									className={
										provider.checked
											? 'text-blue-600 dark:text-blue-400'
											: 'text-green-600 dark:text-green-400'
									}
								>
									{provider.checked ? 'เวอร์ชั่นใหม่' : 'เวอร์ชั่นเริ่มต้น'}
								</Badge>
							</div>
							<Switch
								id={`main-${provider.type}-toggle`}
								checked={provider.checked}
								onCheckedChange={(value) =>
									handleProviderToggle(provider.field, value, provider.type)
								}
								disabled={loadingProvider !== null}
							/>
						</div>
					))}
				</div>
			</CardContent>
		</Card>
	)
}
